import React, { useEffect, useState } from 'react';
import axios from 'axios';
import EventManager from './EventManager';
import DeckManager from './DeckManager';

const ClassDetailPage = ({ classId, onBack }) => {
  const [classData, setClassData] = useState(null);
  const [viewMode, setViewMode] = useState('menu');
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summary, setSummary] = useState('');

  useEffect(() => {
    fetchClassData();
  }, [classId]);

  const fetchClassData = async () => {
    try {
      const res = await axios.get(`https://amora-backend.onrender.com/api/classes/${classId}`);
      setClassData(res.data); // ✅ This triggers rerender
    } catch (err) {
      console.error('Failed to fetch class data:', err);
    }
  };
  const openEditEvent = (event) => {
    setEditingEvent({ ...event, links: event.links || [] });
  };

  const handleEditChange = (e) => {
    setEditingEvent((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleEditLinkChange = (index, value) => {
    const updatedLinks = [...(editingEvent.links || [])];
    updatedLinks[index] = value;
    setEditingEvent((prev) => ({
      ...prev,
      links: updatedLinks,
    }));
  };

  const addEditLinkField = () => {
    setEditingEvent((prev) => ({
      ...prev,
      links: [...(prev.links || []), ''],
    }));
  };

  const saveEditedEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`https://amora-backend.onrender.com/api/classes/${classId}/events/${editingEvent._id}`, editingEvent);
      setEditingEvent(null);
      fetchClassData(); // Refresh events after saving
    } catch (err) {
      console.error('Failed to save event:', err);
    }
  };

  const openPopup = (event) => {
    setSelectedEvent(event);
    setSummary('');
    if (event.disableAI) return;
    setLoadingSummary(true);
    axios.post('https://amora-backend.onrender.com/api/generate-summary', {
      prompt: `Summarize this historical event: ${event.title}, ${event.year}, ${event.description}`,
    })
    .then((res) => {
      setSummary(res.data.summary);
      setLoadingSummary(false);
    })
    .catch((err) => {
      console.error('AI summary error:', err);
      setSummary('⚠️ Failed to load summary.');
      setLoadingSummary(false);
    });
  };

  if (!classData) return <div className="p-8 text-center text-white">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <button onClick={onBack} className="mb-6 text-blue-400 hover:underline">← Back</button>
      <h1 className="text-3xl font-bold text-purple-300 mb-6">{classData.title}</h1>

      {viewMode === 'menu' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div onClick={() => setViewMode('events')} className="bg-gray-800 p-8 rounded-lg shadow hover:bg-gray-700 cursor-pointer flex flex-col items-center justify-center">
            <h2 className="text-2xl font-bold mb-2">📅 Manage Events</h2>
            <p className="text-gray-400 text-center">Create, edit, and delete events for this class.</p>
          </div>
          <div onClick={() => setViewMode('decks')} className="bg-gray-800 p-8 rounded-lg shadow hover:bg-gray-700 cursor-pointer flex flex-col items-center justify-center">
            <h2 className="text-2xl font-bold mb-2">📂 Manage Decks</h2>
            <p className="text-gray-400 text-center">Create decks and organize events into them.</p>
          </div>
        </div>
      )}

      {viewMode === 'events' && (
        <div>
          <button onClick={() => setViewMode('menu')} className="mb-4 text-blue-400 hover:underline">← Back to Menu</button>
          <EventManager
            classId={classId}
            classData={classData}
            fetchClassData={fetchClassData}
            openEditEvent={openEditEvent}
            openPopup={openPopup}
          />
        </div>
      )}

      {viewMode === 'decks' && (
        <div>
          <button onClick={() => setViewMode('menu')} className="mb-4 text-blue-400 hover:underline">← Back to Menu</button>
          <DeckManager classId={classId} classData={classData} fetchClassData={fetchClassData} />
        </div>
      )}

{selectedEvent && (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
    <div className="bg-gray-900 p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl relative">
      <button
        onClick={() => setSelectedEvent(null)}
        className="absolute top-3 right-4 text-white text-xl font-bold"
      >
        ✖
      </button>

      <h3 className="text-2xl font-bold text-blue-300 mb-2">{selectedEvent.title}</h3>
      <p className="text-sm text-gray-400">📅 {selectedEvent.year}</p>
      <p className="text-sm text-gray-400">🏷 {selectedEvent.category}</p>
      <p className="text-sm text-gray-300 mt-4">{selectedEvent.description}</p>

      {/* 🔥 Show AI summary if not disabled */}
      {!selectedEvent.disableAI && (
        <>
          <h4 className="text-lg font-semibold text-purple-300 mt-6">🔎 AI Summary</h4>
          {loadingSummary ? (
            <p className="text-gray-400 mt-2">⏳ Loading AI summary...</p>
          ) : (
            <p className="text-sm text-gray-300 whitespace-pre-wrap mt-2">
              {summary || 'No AI summary available.'}
            </p>
          )}
        </>
      )}

      {/* 🔥 Always show Teacher/Professor manual summary if exists */}
      {selectedEvent.summary && (
        <>
          <h4 className="text-lg font-semibold text-green-400 mt-6">📜 Teacher's Explanation</h4>
          <p className="text-sm text-gray-300 whitespace-pre-wrap mt-2">
            {selectedEvent.summary}
          </p>
        </>
      )}

      {/* 🔗 Useful links if exist */}
      {selectedEvent.links && selectedEvent.links.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-purple-300">🔗 Useful Links:</h4>
          <ul className="list-disc list-inside text-blue-400 text-sm">
            {selectedEvent.links.map((link, idx) => (
              <li key={idx}>
                <a
                  href={link.startsWith('http') ? link : `https://${link}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-200"
                >
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
)}

      {/* Edit Modal */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl relative">
            <button onClick={() => setEditingEvent(null)} className="absolute top-3 right-4 text-white text-xl font-bold">✖</button>

            <h3 className="text-xl font-bold mb-4 text-purple-400">✏️ Edit Event</h3>

            <form onSubmit={saveEditedEvent} className="space-y-4">
              <input name="title" value={editingEvent.title} onChange={handleEditChange} placeholder="Title" className="w-full px-4 py-2 rounded bg-gray-700 text-white" />
              <input name="year" value={editingEvent.year} onChange={handleEditChange} placeholder="Year" type="number" className="w-full px-4 py-2 rounded bg-gray-700 text-white" />
              <input name="category" value={editingEvent.category} onChange={handleEditChange} placeholder="Category" className="w-full px-4 py-2 rounded bg-gray-700 text-white" />
              <textarea name="description" value={editingEvent.description} onChange={handleEditChange} placeholder="Short Description" className="w-full px-4 py-2 rounded bg-gray-700 text-white" rows="3" />
              <textarea name="summary" value={editingEvent.summary} onChange={handleEditChange} placeholder="Summary" className="w-full px-4 py-2 rounded bg-gray-700 text-white" rows="4" />

              {(editingEvent.links || []).map((link, idx) => (
                <input key={idx} value={link} onChange={(e) => handleEditLinkChange(idx, e.target.value)} placeholder={`Link ${idx + 1}`} className="w-full px-4 py-2 rounded bg-gray-700 text-white" />
              ))}

              <button type="button" onClick={addEditLinkField} className="text-blue-400 hover:underline text-sm">➕ Add another link</button>

              <label className="flex items-center gap-2 text-sm mt-4">
                <input
                  type="checkbox"
                  checked={editingEvent.disableAI || false}
                  onChange={(e) => setEditingEvent((prev) => ({ ...prev, disableAI: e.target.checked }))}
                />
                Turn off AI summary
              </label>

              <button type="submit" className="w-full bg-green-600 hover:bg-green-700 py-2 rounded-lg font-semibold mt-4">
                ✅ Save Changes
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassDetailPage;
